"use client";

import {
  IconAlertWarningOutline18,
  IconArrowRotateAnticlockwiseOutline18,
  IconCheckOutline18,
  IconChevronDownOutline18,
  IconChevronRightOutline18,
  IconCodeCompareOutline18,
  IconCopyOutline18,
  IconHeadphonesOutline18,
  IconSparkle3Outline18,
  IconXmarkOutline18,
} from "nucleo-ui-outline-18";
/**
 * UnifiedErrorPanel
 *
 * The single error display component used everywhere in B3OS.
 * Replaces all fragmented error displays with a unified view that includes:
 * - Parsed error message (text + JSON)
 * - AI-powered analysis (auto for test runs, manual button for others)
 * - Fixable field suggestions with one-click apply
 * - Version diff + rollback to last successful version
 * - Full error details with Formatted/JSON toggle
 * - Fix with Caddie fallback
 */

import { CaddieAvatar } from "@/components/caddie/CaddieAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PixelLoader } from "@/components/ui/pixel-loader";
import type { FixWithAIErrorContext } from "@/hooks/useFixWithAI";
import { FIX_WITH_CADDIE_LABEL } from "@/lib/caddie";
import { openSupport } from "@/lib/support";
import { getSyntaxHighlightingStyle } from "@/lib/syntax-highlighting-style";
import { cn } from "@/lib/utils";
import { computeVersionDiff, type VersionDiffSummary } from "@/lib/workflow/versionDiff";
import { useAIChatStore } from "@/stores/aiChatStore";
import type { ErrorInfo, NodeExecutionState } from "@/types/runStream";
import { useColorMode } from "@chakra-ui/react";
import { useCallback, useId, useMemo, useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { ErrorStatusBadges } from "./ErrorCategoryBadge";
import { ErrorFieldEditorList } from "./ErrorFieldEditor";
import { useErrorAnalysis, type ErrorAnalysisContext } from "./useErrorAnalysis";
import { useVersionCompareData } from "./useVersionCompareData";
import { CompareChangesList, fetchVersionWorkflow } from "./versionCompareUtils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UnifiedErrorPanelProps {
  /** The error message string */
  error: string;
  /** Full node execution state (for input/output context) */
  nodeState?: NodeExecutionState;
  /** Node metadata */
  nodeInfo?: { name?: string; type?: string; category?: string };
  /** Node configuration/payload (url, method, headers, etc.) */
  nodeConfig?: Record<string, unknown>;

  /** Run context */
  runId?: string;
  workflowId?: string;
  /** Whether this error came from a test button run (auto-triggers AI) */
  isTestRun?: boolean;

  /** Display variant */
  variant: "inline" | "modal" | "compact";

  /** Feature toggles */
  showAIAnalysis?: boolean;
  showFieldEditor?: boolean;
  showVersionCompare?: boolean;
  showFixWithAI?: boolean;
  /** Whether to show the "Get Support" button (default true). Set false when parent already provides one. */
  showGetSupport?: boolean;

  /** Callbacks */
  onFixWithAI?: (errorCtx?: FixWithAIErrorContext) => void;
  onFieldUpdate?: (fieldName: string, value: unknown) => void;
  /** Called when user clicks rollback to a previous version */
  onRollback?: (targetVersion: number) => void;
  /** Called when user clicks "Get Support" */
  onGetSupport?: () => void;
}

// ---------------------------------------------------------------------------
// Error parsing (extracted from existing pattern used across codebase)
// ---------------------------------------------------------------------------

interface ParsedError {
  textParts: string[];
  jsonBlocks: string[];
  parsedObjects: Record<string, unknown>[];
}

const STRUCTURED_ERROR_KEYS = ["details", "detail", "message", "error_description", "reason", "error"] as const;

// Nested-quantifier regex can ReDoS on long strings with many unbalanced braces.
// Skip extraction for oversized inputs and treat them as plain text.
const MAX_JSON_EXTRACT_LEN = 10_000;

function parseErrorMessage(error: string): ParsedError {
  if (error.length > MAX_JSON_EXTRACT_LEN) {
    return { textParts: [error], jsonBlocks: [], parsedObjects: [] };
  }
  const jsonRegex = /\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g;
  const matches = error.match(jsonRegex);

  if (!matches || matches.length === 0) {
    return { textParts: [error], jsonBlocks: [], parsedObjects: [] };
  }

  const textParts: string[] = [];
  const jsonBlocks: string[] = [];
  const parsedObjects: Record<string, unknown>[] = [];
  let lastIndex = 0;

  for (const match of matches) {
    const index = error.indexOf(match, lastIndex);
    if (index > lastIndex) {
      const text = error.substring(lastIndex, index).trim();
      if (text) textParts.push(text);
    }
    try {
      const parsed = JSON.parse(match);
      jsonBlocks.push(JSON.stringify(parsed, null, 2));
      parsedObjects.push(parsed);
    } catch {
      textParts.push(match);
    }
    lastIndex = index + match.length;
  }

  if (lastIndex < error.length) {
    const text = error.substring(lastIndex).trim();
    if (text) textParts.push(text);
  }

  return { textParts, jsonBlocks, parsedObjects };
}

/** Map of technical error patterns to friendly, non-technical descriptions */
const FRIENDLY_ERROR_PATTERNS: [RegExp, string | ((m: RegExpMatchArray) => string)][] = [
  // Executor / node type issues
  [
    /executor not found for action type ([^\s:]+)/i,
    m => `"${m[1]}" action is not available — it may need to be reinstalled or updated`,
  ],
  [/no executor registered for node type/i, "This action type isn't supported yet"],
  // Auth / permissions (use word boundaries to avoid matching hex calldata)
  [/\bunauthorized\b|\b401\b/i, "Authentication failed — check your connector or API key"],
  [/\bforbidden\b|\b403\b/i, "Access denied — you don't have permission for this resource"],
  // Rate limiting (use word boundaries to avoid matching hex calldata)
  [/\brate.?limit|\b429\b|\btoo many requests\b/i, "Rate limited — wait a moment and try again"],
  // Timeout
  [/timeout|timed?\s*out|deadline exceeded/i, "Request timed out — the external service took too long to respond"],
  // Connection errors
  [/ECONNREFUSED|connection refused/i, "Could not connect to the external service"],
  [/ENOTFOUND|DNS|name resolution/i, "Could not find the server — check the URL"],
  [/network|ENETUNREACH/i, "Network error — check your connection"],
  // HTTP errors (use word boundaries to avoid matching hex calldata)
  [/\b400\b.*\bbad request\b|\bbad request\b/i, "The request was rejected — check your input fields"],
  [/\b404\b.*\bnot found\b|\bnot found\b/i, "Resource not found — check the URL or ID"],
  [/\b500\b|\binternal server error\b/i, "The external service encountered an internal error"],
  [/\b502\b|\bbad gateway\b/i, "The external service is temporarily unreachable"],
  [/\b503\b|\bservice unavailable\b/i, "The external service is temporarily down"],
  // Validation
  [/required field|missing required|is required/i, "A required field is missing — check your configuration"],
  [/invalid.*json|JSON.*parse|unexpected token/i, "Invalid JSON format in the request body"],
  [/invalid.*url|malformed.*url/i, "The URL is invalid — check for typos"],
  // Insufficient balance (blockchain)
  [
    /\binsufficient.?balance\b|\binsufficient.?funds\b|\btransfer amount exceeds balance\b/i,
    "Insufficient token balance for this transaction",
  ],
  [/gas/i, "Transaction failed due to gas estimation — check token balance or parameters"],
];

/**
 * Extract a short, human-readable summary from an error string.
 * Purely client-side — no AI needed.
 */
export function summarizeError(error: string): string {
  if (!error) return "Error";

  // Check friendly patterns first
  for (const [pattern, friendly] of FRIENDLY_ERROR_PATTERNS) {
    const match = error.match(pattern);
    if (match) {
      return typeof friendly === "function" ? friendly(match) : friendly;
    }
  }

  // Try to extract a message from embedded JSON (e.g. {"message":"some error","code":400})
  const jsonMatch = error.match(/\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/);
  if (jsonMatch) {
    try {
      const obj = JSON.parse(jsonMatch[0]);
      const msg = obj.message || obj.error || obj.detail || obj.error_description;
      if (typeof msg === "string" && msg.length > 0) {
        return truncateSummary(msg);
      }
    } catch {
      // not valid JSON, continue
    }
  }

  // Clean up common technical prefixes and give a simpler message
  const cleaned = error
    .replace(/^(execution error|Error|error|ERROR)[:\s]+/i, "")
    .replace(/^(node execution failed)[:\s]*/i, "")
    .trim();

  if (!cleaned) return "Something went wrong";

  return truncateSummary(cleaned);
}

function findPreferredStructuredMessage(value: unknown): string | null {
  if (!value || typeof value !== "object") return null;

  const queue: unknown[] = [value];
  const seen = new Set<unknown>();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== "object" || seen.has(current)) continue;
    seen.add(current);

    const record = current as Record<string, unknown>;
    for (const key of STRUCTURED_ERROR_KEYS) {
      const candidate = record[key];
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate.trim();
      }
    }

    for (const nested of Object.values(record)) {
      if (nested && typeof nested === "object") queue.push(nested);
    }
  }

  return null;
}

export function getPrimaryErrorMessage(error: string): string {
  if (!error) return "Something went wrong";

  const parsed = parseErrorMessage(error);
  for (const obj of parsed.parsedObjects) {
    const structured = findPreferredStructuredMessage(obj);
    if (structured) return truncateSummary(structured);
  }

  return summarizeError(error);
}

/**
 * Resolve the structured `errorInfo` from a node state, checking both the
 * top-level field (populated by SSE node_failed events) and the nested
 * `result.errorInfo` field (populated when loading historical runs from
 * the REST API). The two paths produce slightly different shapes — this
 * helper unifies them for the rest of the UI.
 */
export function resolveErrorInfo(nodeState?: {
  errorInfo?: ErrorInfo;
  result?: Record<string, unknown>;
}): ErrorInfo | undefined {
  if (nodeState?.errorInfo) return nodeState.errorInfo;
  const nestedRaw = nodeState?.result?.errorInfo;
  if (nestedRaw && typeof nestedRaw === "object") {
    const candidate = nestedRaw as Record<string, unknown>;
    if (typeof candidate.code === "string" && typeof candidate.category === "string") {
      return nestedRaw as ErrorInfo;
    }
  }
  return undefined;
}

/**
 * Prefer the structured `errorInfo.displayMessage` from the backend when
 * available; fall back to the legacy regex-based summarizer for older runs.
 */
export function getPrimaryDisplayMessage(nodeState?: NodeExecutionState, fallbackError?: string): string {
  const info = resolveErrorInfo(nodeState);
  if (info?.displayMessage) {
    return info.displayMessage;
  }
  return getPrimaryErrorMessage(fallbackError ?? nodeState?.error ?? "");
}

function truncateSummary(text: string): string {
  // Spread to iterate by codepoint instead of UTF-16 unit (slice splits
  // surrogate pairs).
  const firstSentence = text.split(/[.\n]/)[0]?.trim();
  const summary = firstSentence || text;
  const codepoints = [...summary];
  if (codepoints.length > 100) return codepoints.slice(0, 97).join("") + "...";
  return summary;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CopyButton({ text, className }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    },
    [text],
  );

  return (
    <button
      onClick={handleCopy}
      className={cn(
        "hover:bg-ds-neutral-200 dark:hover:bg-ds-neutral-700 rounded-md p-1.5 transition-colors",
        className,
      )}
      title="Copy to clipboard"
    >
      {copied ? (
        <IconCheckOutline18 className="h-3.5 w-3.5 text-green-500" />
      ) : (
        <IconCopyOutline18 className="text-ds-text-muted h-3.5 w-3.5" />
      )}
    </button>
  );
}

/** Renders a parsed error value recursively (for formatted view) */
function ErrorValue({ value, depth = 0 }: { value: unknown; depth?: number }): React.ReactElement {
  if (value === null || value === undefined) {
    return <span className="text-ds-text-disabled">null</span>;
  }
  if (typeof value === "boolean") {
    return (
      <span className={value ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
        {String(value)}
      </span>
    );
  }
  if (typeof value === "number") {
    return <span className="text-blue-600 dark:text-blue-400">{value}</span>;
  }
  if (typeof value === "string") {
    const lines = value.split(/\\n|\n/);
    if (lines.length > 1) {
      return (
        <div className="space-y-0.5">
          {lines.map((line, i) => (
            <div key={i} className="text-ds-text-secondary break-all">
              {line || <span className="text-ds-text-disabled">&nbsp;</span>}
            </div>
          ))}
        </div>
      );
    }
    if (value.length > 200) {
      return (
        <span className="text-ds-text-secondary break-all" title={value}>
          {value.slice(0, 200)}...
        </span>
      );
    }
    return <span className="text-ds-text-secondary break-all">{value}</span>;
  }
  if (Array.isArray(value)) {
    return (
      <div className="space-y-1">
        {value.map((item, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="text-ds-text-disabled">[{i}]</span>
            <ErrorValue value={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }
  if (typeof value === "object") {
    return (
      <div className={cn("space-y-2", depth > 0 && "border-ds-border-default border-l-2 pl-3")}>
        {Object.entries(value as Record<string, unknown>).map(([k, v]) => (
          <div key={k} className="flex flex-col gap-1">
            <span className="text-ds-text-subtle dark:text-ds-text-disabled text-xs font-semibold uppercase">{k}</span>
            <ErrorValue value={v} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }
  return <span>{String(value)}</span>;
}

/** Renders a formatted error object with special handling for code/success fields */
function FormattedErrorObject({ obj }: { obj: Record<string, unknown> }) {
  return (
    <div className="divide-ds-neutral-200 dark:divide-ds-neutral-700 divide-y">
      {Object.entries(obj).map(([key, value]) => {
        if (key === "code" && typeof value === "string") {
          return (
            <div key={key} className="py-2.5 first:pt-0 last:pb-0">
              <div className="text-ds-text-muted mb-1 text-xs font-semibold uppercase tracking-wider">{key}</div>
              <span className="text-sm font-semibold text-red-700 dark:text-red-400">{value}</span>
            </div>
          );
        }
        if (key === "success" && typeof value === "boolean") {
          return (
            <div key={key} className="py-2.5 first:pt-0 last:pb-0">
              <div className="text-ds-text-muted mb-1 text-xs font-semibold uppercase tracking-wider">{key}</div>
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 text-sm font-medium",
                  value ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400",
                )}
              >
                {value ? <IconCheckOutline18 className="h-3 w-3" /> : <IconXmarkOutline18 className="h-3 w-3" />}
                {value ? "Success" : "Failed"}
              </span>
            </div>
          );
        }
        return (
          <div key={key} className="py-2.5 first:pt-0 last:pb-0">
            <div className="text-ds-text-muted mb-1 text-xs font-semibold uppercase tracking-wider">{key}</div>
            <div className="text-sm">
              <ErrorValue value={value} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI Analysis Section
// ---------------------------------------------------------------------------

function AIAnalysisDisplay({
  analysis,
  isAnalyzing,
  isCached,
  triggerAnalysis,
  reanalyze,
  showFieldEditor,
  onFieldUpdate,
}: {
  analysis: ReturnType<typeof useErrorAnalysis>["analysis"];
  isAnalyzing: boolean;
  isCached: boolean;
  triggerAnalysis: () => void;
  reanalyze: () => void;
  showFieldEditor?: boolean;
  onFieldUpdate?: (fieldName: string, value: unknown) => void;
}) {
  const [appliedFields, setAppliedFields] = useState<Set<string>>(new Set());

  const handleAppliedFieldsChange = useCallback((fields: Set<string>) => {
    setAppliedFields(new Set(fields));
  }, []);

  // Not yet analyzed and no previous analysis to show — show trigger button
  if (!analysis && !isAnalyzing) {
    return (
      <button
        onClick={triggerAnalysis}
        className="flex w-full items-center gap-2 rounded-lg border border-dashed border-blue-300 bg-blue-50/50 px-3 py-2.5 text-left transition-colors hover:border-blue-400 hover:bg-blue-50 dark:border-blue-800 dark:bg-blue-950/20 dark:hover:border-blue-700 dark:hover:bg-blue-950/30"
      >
        <IconSparkle3Outline18 className="h-4 w-4 text-blue-500" />
        <span className="text-xs font-medium text-blue-700 dark:text-blue-400">Analyze Error with AI</span>
      </button>
    );
  }

  // Pure loading state (no previous analysis to show)
  if (isAnalyzing && !analysis) {
    return (
      <div className="flex items-end gap-3">
        <CaddieAvatar size={40} mood="building" decorated={false} className="shrink-0 self-end" />
        <div className="min-w-0 flex-1">
          <div className="rounded-2xl rounded-bl-md border border-blue-200 bg-blue-50/60 px-4 py-3 dark:border-blue-900/50 dark:bg-blue-950/20">
            <div className="flex items-center gap-2 text-sm text-zinc-800 dark:text-zinc-200">
              <PixelLoader size="sm" color="blue" />
              <span>Analyzing error...</span>
            </div>
            <div className="mt-3 space-y-2">
              <div className="h-3 w-3/4 animate-pulse rounded bg-blue-100 dark:bg-blue-900/30" />
              <div className="h-3 w-1/2 animate-pulse rounded bg-blue-100 dark:bg-blue-900/30" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Analysis result (or previous analysis while re-analyzing)
  // Guard against empty analysis text (e.g. from a failed/empty LLM response)
  if (!analysis || !analysis.analysis?.trim()) return null;

  return (
    <div className={cn("space-y-3", isAnalyzing && "opacity-60 transition-opacity")}>
      <div className="flex items-end gap-3">
        <CaddieAvatar
          size={40}
          mood={isAnalyzing ? "building" : "conversation"}
          decorated={false}
          className="shrink-0 self-end"
        />
        <div className="min-w-0 flex-1">
          <div className="rounded-2xl rounded-bl-md border border-blue-200 bg-blue-50/60 px-4 py-3 dark:border-blue-900/50 dark:bg-blue-950/20">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-800 dark:text-zinc-200">
              {analysis.analysis}
            </p>

            {analysis.suggestedFix && (
              <div className="mt-3 border-t border-blue-200 pt-3 dark:border-blue-800/50">
                <p className="label-xs label text-ds-focus-brand mb-2">Suggestion</p>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-blue-600 dark:text-blue-300">
                  {analysis.suggestedFix}
                </p>
              </div>
            )}
          </div>
          <div className="mt-2 flex items-center justify-between">
            {isCached && !isAnalyzing ? (
              <button
                type="button"
                onClick={reanalyze}
                className="flex shrink-0 items-center justify-center"
                title="Re-analyze (bypass cache)"
                aria-label="Re-analyze error"
              >
                <IconArrowRotateAnticlockwiseOutline18 className="hover:text-ds-text-muted dark:text-ds-text-subtle dark:hover:text-ds-text-disabled h-3.5 w-3.5 text-zinc-300" />
              </button>
            ) : null}
          </div>
        </div>
      </div>
      {/* Fixable fields */}
      {showFieldEditor && analysis.fixableFields.length > 0 && onFieldUpdate && (
        <ErrorFieldEditorList
          fields={analysis.fixableFields}
          onApplyFix={onFieldUpdate}
          initialAppliedFields={appliedFields}
          onAppliedFieldsChange={handleAppliedFieldsChange}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline AI Analysis (used inside the consolidated error box, no extra avatar)
// ---------------------------------------------------------------------------

function InlineAIAnalysis({
  analysis,
  isAnalyzing,
  isCached,
  triggerAnalysis,
  reanalyze,
  showFieldEditor,
  onFieldUpdate,
}: {
  analysis: ReturnType<typeof useErrorAnalysis>["analysis"];
  isAnalyzing: boolean;
  isCached: boolean;
  triggerAnalysis: () => void;
  reanalyze: () => void;
  showFieldEditor?: boolean;
  onFieldUpdate?: (fieldName: string, value: unknown) => void;
}) {
  const [appliedFields, setAppliedFields] = useState<Set<string>>(new Set());
  const [suggestionExpanded, setSuggestionExpanded] = useState(false);
  const suggestionId = useId();
  const handleAppliedFieldsChange = useCallback((fields: Set<string>) => {
    setAppliedFields(new Set(fields));
  }, []);

  if (!analysis && !isAnalyzing) {
    return (
      <button
        onClick={triggerAnalysis}
        className="flex w-full items-center gap-2 rounded-lg border border-dashed border-blue-300 bg-blue-50/50 px-3 py-2.5 text-left transition-colors hover:border-blue-400 hover:bg-blue-50 dark:border-blue-800 dark:bg-blue-950/20 dark:hover:border-blue-700 dark:hover:bg-blue-950/30"
      >
        <IconSparkle3Outline18 className="h-4 w-4 text-blue-500" />
        <span className="text-xs font-medium text-blue-700 dark:text-blue-400">Analyze Error with AI</span>
      </button>
    );
  }

  if (isAnalyzing && !analysis) {
    return (
      <div>
        <div className="text-ds-text-subtle dark:text-ds-text-disabled flex items-center gap-2 text-sm">
          <PixelLoader size="xs" color="blue" />
          <span className="text-xs">Analyzing error...</span>
        </div>
      </div>
    );
  }

  if (!analysis || !analysis.analysis?.trim()) return null;

  return (
    <div className={cn(isAnalyzing && "opacity-60 transition-opacity")}>
      <span className="body-sm text-ds-text-secondary inline whitespace-pre-wrap">
        {analysis.analysis}
        {isCached && !isAnalyzing && (
          <button
            type="button"
            onClick={reanalyze}
            className="ml-1.5 inline-flex shrink-0 translate-y-[1px] items-center justify-center"
            title="Re-analyze (bypass cache)"
            aria-label="Re-analyze error"
          >
            <IconArrowRotateAnticlockwiseOutline18 className="hover:text-ds-text-muted dark:text-ds-text-subtle dark:hover:text-ds-text-disabled h-3.5 w-3.5 text-zinc-300" />
          </button>
        )}
      </span>
      {analysis.suggestedFix && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setSuggestionExpanded(prev => !prev)}
            aria-expanded={suggestionExpanded}
            aria-controls={suggestionExpanded ? suggestionId : undefined}
            className="label label-xs text-ds-focus-brand flex items-center gap-1.5 py-1.5 transition-colors hover:text-blue-700 dark:hover:text-blue-300"
          >
            {suggestionExpanded ? (
              <IconChevronDownOutline18 className="h-3 w-3" />
            ) : (
              <IconChevronRightOutline18 className="h-3 w-3" />
            )}
            Suggestion
          </button>
          {suggestionExpanded && (
            <p id={suggestionId} className="body-sm mt-1 whitespace-pre-wrap text-blue-600 dark:text-blue-300">
              {analysis.suggestedFix}
            </p>
          )}
        </div>
      )}
      {showFieldEditor && analysis.fixableFields.length > 0 && onFieldUpdate && (
        <div className="mt-2">
          <ErrorFieldEditorList
            fields={analysis.fixableFields}
            onApplyFix={onFieldUpdate}
            initialAppliedFields={appliedFields}
            onAppliedFieldsChange={handleAppliedFieldsChange}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Version Compare Link (shown below AI analysis when there's a prior success)
// ---------------------------------------------------------------------------

function VersionCompareLink({
  workflowId,
  runId,
  onRollback,
}: {
  workflowId?: string;
  runId?: string;
  onRollback?: (targetVersion: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [diff, setDiff] = useState<VersionDiffSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { canCompare, failedVersion, successVersion, resolvedWorkflowId } = useVersionCompareData(workflowId, runId);

  const handleExpand = useCallback(async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (diff || !resolvedWorkflowId || failedVersion == null || successVersion == null) return;
    setIsLoading(true);
    try {
      const [successWf, failedWf] = await Promise.all([
        fetchVersionWorkflow(resolvedWorkflowId, successVersion),
        fetchVersionWorkflow(resolvedWorkflowId, failedVersion),
      ]);
      if (successWf && failedWf) {
        setDiff(computeVersionDiff(failedWf, successWf));
      }
    } catch {
      // ignore
    } finally {
      setIsLoading(false);
    }
  }, [expanded, diff, resolvedWorkflowId, failedVersion, successVersion]);

  if (!resolvedWorkflowId) return null;

  // Return null if no successful run exists to compare against
  if (!canCompare || failedVersion == null || successVersion == null) {
    return null;
  }

  return (
    <div className="border-ds-neutral-200/60 dark:border-ds-neutral-700/40 border-t pt-3">
      <button
        onClick={handleExpand}
        className="flex items-center gap-1.5 text-xs font-medium text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
      >
        <IconCodeCompareOutline18 className="h-3 w-3" />
        Compare with last successful run
        {successVersion !== failedVersion && ` (v${successVersion} → v${failedVersion})`}
      </button>
      {expanded && (
        <div className="border-ds-border-default dark:bg-ds-neutral-800/50 mt-3 rounded-lg border bg-zinc-50 p-4">
          {/* Version pills */}
          <div className="mb-3 flex items-center gap-2">
            <Badge variant="success" className="px-2.5 py-1">
              <span className="h-2 w-2 rounded-full bg-green-500" />v{successVersion} (last success)
            </Badge>
            <span className="text-ds-text-disabled text-xs">→</span>
            <Badge variant="destructive" className="px-2.5 py-1">
              <span className="h-2 w-2 rounded-full bg-red-500" />v{failedVersion} (failed)
            </Badge>
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-4">
              <PixelLoader size="sm" color="blue" />
            </div>
          )}

          {/* Diff content */}
          {diff && !isLoading && (
            <>
              {diff.totalChanges === 0 ? (
                <p className="text-ds-text-muted text-xs">No structural changes detected between these versions.</p>
              ) : (
                <>
                  <p className="text-ds-text-muted mb-2 text-xs">
                    {diff.totalChanges} change{diff.totalChanges !== 1 ? "s" : ""} between versions — the failure may be
                    caused by these modifications.
                  </p>
                  <CompareChangesList diff={diff} />
                </>
              )}
            </>
          )}

          {/* Rollback button */}
          {onRollback && successVersion != null && (
            <div className="mt-3 flex justify-end">
              <button
                onClick={() => onRollback(successVersion)}
                className="bg-ds-neutral-100 text-ds-text-secondary hover:bg-ds-neutral-200 dark:bg-ds-neutral-700 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors dark:hover:bg-zinc-600"
              >
                <IconArrowRotateAnticlockwiseOutline18 className="h-3 w-3" />
                Revert to v{successVersion}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fix with Caddie Button (works with or without onFixWithAI callback)
// ---------------------------------------------------------------------------

function FixWithCaddieButton({
  error,
  nodeInfo,
  onFixWithAI,
}: {
  error: string;
  nodeInfo?: { name?: string; type?: string; category?: string };
  onFixWithAI?: (errorCtx?: FixWithAIErrorContext) => void;
}) {
  const openPanel = useAIChatStore(state => state.openPanel);
  const setPendingMessage = useAIChatStore(state => state.setPendingMessage);

  const handleClick = useCallback(() => {
    if (onFixWithAI) {
      onFixWithAI({
        nodeName: nodeInfo?.name,
        nodeType: nodeInfo?.type,
        errorMessage: error,
      });
    } else {
      // Default behavior: open AI chat with error context
      const nodePart = nodeInfo?.name
        ? `in my ${nodeInfo.name} node`
        : nodeInfo?.type
          ? `in my ${nodeInfo.type} node`
          : "";
      const message = `Fix this error ${nodePart}:\n${error.slice(0, 500)}`;
      setPendingMessage(message);
      openPanel();
    }
  }, [error, nodeInfo, onFixWithAI, openPanel, setPendingMessage]);

  return (
    <Button onClick={handleClick} className="gap-1.5 px-3 text-xs">
      <CaddieAvatar size={16} mood="repair" decorated={false} />
      {FIX_WITH_CADDIE_LABEL}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function UnifiedErrorPanel({
  error,
  nodeState,
  nodeInfo,
  nodeConfig,
  runId,
  workflowId,
  isTestRun = false,
  variant = "inline",
  showAIAnalysis = true,
  showFieldEditor = false,
  showVersionCompare = true,
  showFixWithAI = true,
  showGetSupport = true,
  onFixWithAI,
  onFieldUpdate,
  onRollback,
  onGetSupport,
}: UnifiedErrorPanelProps) {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const syntaxStyle = getSyntaxHighlightingStyle(isDark);
  const [viewMode, setViewMode] = useState<"auto" | "json">("auto");
  const [rawErrorExpanded, setRawErrorExpanded] = useState(false);

  // Default Get Support handler — opens RogerIQ widget with error context
  const handleGetSupport = useCallback(() => {
    if (onGetSupport) {
      onGetSupport();
      return;
    }
    const nodeLabel = nodeInfo?.name || nodeInfo?.type;
    const subject = nodeLabel ? `Help with step "${nodeLabel}"` : "Help with a workflow error";
    const text = [runId ? `Run ID: ${runId}` : null, error ? `Error: ${error}` : null].filter(Boolean).join("\n");
    openSupport({ subject, text: text || undefined });
  }, [onGetSupport, nodeInfo?.name, nodeInfo?.type, runId, error]);

  // Parse the error message
  const parsed = useMemo(() => parseErrorMessage(error), [error]);

  // Prefer structured errorInfo from the backend over regex-summarized strings.
  const errorInfo = resolveErrorInfo(nodeState);
  const primaryDisplayMessage = useMemo(() => getPrimaryDisplayMessage(nodeState, error), [nodeState, error]);

  // Build AI analysis context
  const analysisContext: ErrorAnalysisContext | null = useMemo(() => {
    if (!showAIAnalysis) return null;
    return {
      nodeType: nodeInfo?.type,
      nodeName: nodeInfo?.name,
      errorMessage: error,
      nodeInput: nodeState?.input,
      nodeOutput: nodeState?.result,
      nodeConfig,
      runId,
      workflowId,
      isTestRun,
    };
  }, [showAIAnalysis, nodeInfo, error, nodeState?.input, nodeState?.result, nodeConfig, runId, workflowId, isTestRun]);

  // Call useErrorAnalysis at top level so we can check analysis state
  const { analysis, isAnalyzing, isCached, triggerAnalysis, reanalyze } = useErrorAnalysis(analysisContext);

  // -------------------------------------------------------------------------
  // Compact variant (for toolbars / inline hints)
  // -------------------------------------------------------------------------
  if (variant === "compact") {
    return (
      <div className="space-y-2">
        {/* Category badges (only when structured errorInfo is present) */}
        {errorInfo && (
          <div className="flex flex-wrap items-center gap-1.5">
            <ErrorStatusBadges errorInfo={errorInfo} />
          </div>
        )}
        {/* Error summary */}
        <div className="flex items-start gap-2">
          <IconAlertWarningOutline18 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-red-500" />
          <p className="line-clamp-2 text-xs text-red-600 dark:text-red-400">{primaryDisplayMessage}</p>
        </div>
        {/* Remediation hint */}
        {errorInfo?.remediation && (
          <p className="text-ds-text-subtle dark:text-ds-text-disabled pl-5 text-xs">{errorInfo.remediation}</p>
        )}
        {/* AI analysis */}
        {analysisContext && (
          <AIAnalysisDisplay
            analysis={analysis}
            isAnalyzing={isAnalyzing}
            isCached={isCached}
            triggerAnalysis={triggerAnalysis}
            reanalyze={reanalyze}
            showFieldEditor={showFieldEditor}
            onFieldUpdate={onFieldUpdate}
          />
        )}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Inline / Modal variant (full-featured)
  // -------------------------------------------------------------------------
  return (
    <div className="mt-6 rounded-xl border border-dashed border-red-400 bg-red-50/50 px-4 py-3 dark:border-red-800/50 dark:bg-red-950/20">
      {/* Header: Caddie avatar + error summary + copy */}
      <div className="flex items-start gap-3">
        <CaddieAvatar size={40} mood="planning" decorated={false} className="shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="label label-xs text-ds-text-primary dark:text-zinc-100">What Happened</p>
            {errorInfo && <ErrorStatusBadges errorInfo={errorInfo} />}
          </div>
          {/* Fallback: show human-readable summary when AI analysis is absent or empty */}
          {(!analysis || !analysis.analysis?.trim()) && primaryDisplayMessage && (
            <p className="body-sm mt-1.5 text-red-600 dark:text-red-400">{primaryDisplayMessage}</p>
          )}
          {errorInfo?.remediation && (
            <p className="body-sm text-ds-text-subtle dark:text-ds-text-disabled mt-1.5">{errorInfo.remediation}</p>
          )}
        </div>
        <CopyButton text={error} className="mt-[-2px] shrink-0" />
      </div>
      {/* Content below — aligned with the text column (offset past the avatar) */}
      <div className="ml-[52px]">
        {/* AI Analysis */}
        {analysisContext && (
          <div className="mt-2">
            <InlineAIAnalysis
              analysis={analysis}
              isAnalyzing={isAnalyzing}
              isCached={isCached}
              triggerAnalysis={triggerAnalysis}
              reanalyze={reanalyze}
              showFieldEditor={showFieldEditor}
              onFieldUpdate={onFieldUpdate}
            />
          </div>
        )}

        {/* Version compare */}
        {showVersionCompare && (
          <div className="mt-3">
            <VersionCompareLink workflowId={workflowId} runId={runId} onRollback={onRollback} />
          </div>
        )}

        {/* Error Details */}
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setRawErrorExpanded(!rawErrorExpanded)}
            className="label label-xs text-ds-text-primary hover:text-ds-text-subtle flex items-center gap-1.5 py-1.5 transition-colors dark:text-zinc-100 dark:hover:text-zinc-300"
          >
            {rawErrorExpanded ? (
              <IconChevronDownOutline18 className="h-3 w-3" />
            ) : (
              <IconChevronRightOutline18 className="h-3 w-3" />
            )}
            Error Details
          </button>
          {rawErrorExpanded && (
            <div className="mt-2">
              <RawErrorContent
                parsed={parsed}
                viewMode={viewMode}
                setViewMode={setViewMode}
                syntaxStyle={syntaxStyle}
              />
            </div>
          )}
        </div>

        {/* Action buttons */}
        {(showGetSupport || showFixWithAI) && (
          <div className="mt-4 flex items-center justify-end gap-2 border-t border-red-200/60 pt-3 dark:border-red-800/30">
            {showGetSupport && (
              <Button onClick={handleGetSupport} variant="white" className="gap-1.5 px-3 text-xs">
                <IconHeadphonesOutline18 className="h-3.5 w-3.5" />
                Get Support
              </Button>
            )}
            {showFixWithAI && <FixWithCaddieButton error={error} nodeInfo={nodeInfo} onFixWithAI={onFixWithAI} />}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Raw Error Content (extracted for reuse in accordion vs inline)
// ---------------------------------------------------------------------------

function RawErrorContent({
  parsed,
  viewMode,
  setViewMode,
  syntaxStyle,
}: {
  parsed: ParsedError;
  viewMode: "auto" | "json";
  setViewMode: (mode: "auto" | "json") => void;
  syntaxStyle: Record<string, React.CSSProperties>;
}) {
  // Text-only error (no JSON blocks)
  if (parsed.jsonBlocks.length === 0) {
    return (
      <div className="space-y-1.5">
        {parsed.textParts.map((text, i) => (
          <p key={i} className="whitespace-pre-wrap break-all text-sm text-red-600 dark:text-red-400">
            {text}
          </p>
        ))}
      </div>
    );
  }

  // Error with JSON blocks — show with Formatted/JSON toggle
  return (
    <div>
      {/* View mode toggle */}
      <div className="border-ds-border-default bg-ds-neutral-100 dark:bg-ds-neutral-800 mb-3 flex items-center gap-1 rounded-lg border p-1">
        <button
          onClick={() => setViewMode("auto")}
          className={cn(
            "flex-1 rounded-md px-3 py-1.5 text-xs font-medium uppercase tracking-wider transition-colors",
            viewMode === "auto"
              ? "text-ds-text-primary shadow-ds-soft bg-ds-surface-card"
              : "text-ds-text-subtle hover:text-ds-text-primary dark:text-ds-text-disabled dark:hover:text-white",
          )}
        >
          Formatted
        </button>
        <button
          onClick={() => setViewMode("json")}
          className={cn(
            "flex-1 rounded-md px-3 py-1.5 text-xs font-medium uppercase tracking-wider transition-colors",
            viewMode === "json"
              ? "text-ds-text-primary shadow-ds-soft bg-ds-surface-card"
              : "text-ds-text-subtle hover:text-ds-text-primary dark:text-ds-text-disabled dark:hover:text-white",
          )}
        >
          JSON
        </button>
      </div>

      {/* Formatted view */}
      {viewMode === "auto" ? (
        <div className="space-y-4">
          {parsed.parsedObjects.map((obj, i) => (
            <FormattedErrorObject key={i} obj={obj} />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {parsed.jsonBlocks.map((json, i) => (
            <div
              key={i}
              className="border-ds-border-default dark:bg-ds-neutral-800 relative overflow-hidden rounded-md border bg-zinc-50"
            >
              <CopyButton text={json} className="absolute right-2 top-2 z-10" />
              <SyntaxHighlighter
                language="json"
                style={syntaxStyle}
                customStyle={{
                  margin: 0,
                  padding: "0.75rem",
                  fontSize: "0.75rem",
                  lineHeight: "1.5",
                  background: "transparent",
                  overflowX: "auto",
                }}
                wrapLines
                wrapLongLines
              >
                {json}
              </SyntaxHighlighter>
            </div>
          ))}
        </div>
      )}

      {/* Text parts alongside JSON */}
      {parsed.textParts.length > 0 && (
        <div className="mt-3 space-y-1">
          {parsed.textParts.map((text, i) => (
            <p key={i} className="text-xs text-red-500 dark:text-red-400">
              {text}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
